/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { List, ListItemButton, Paper, Stack, Typography } from '@mui/material';
import type { ProjectCoordinatesEntry } from '@poweralpha/common';
import { useState } from 'react';

interface Props {
  projects: ProjectCoordinatesEntry[];
  selectedProjectId: string;
  onSelectProject: (project: ProjectCoordinatesEntry) => void;
}

function ProjectsMapHoverCard({ projects, selectedProjectId, onSelectProject }: Readonly<Props>) {
  const [hoveredProjectId, setHoveredProjectId] = useState<string | null>(null);

  if (projects.length === 1) {
    const [project] = projects;

    return (
      <Paper
        elevation={6}
        role="button"
        tabIndex={0}
        onClick={() => onSelectProject(project)}
        sx={{
          borderRadius: 2,
          minWidth: 180,
          maxWidth: 220,
          px: 1,
          py: 0.75,
          cursor: 'pointer',
        }}
      >
        <Stack spacing={0.25}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.1,
            }}
          >
            Project
          </Typography>
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.2,
            }}
          >
            {project.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.1,
            }}
          >
            {project.plant_type ?? 'Plant type unavailable'}
          </Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper elevation={6} sx={{ borderRadius: 2, minWidth: 180, maxWidth: 240, p: 0.5 }}>
      <List dense disablePadding onMouseLeave={() => setHoveredProjectId(null)}>
        {projects.map((project) => (
          <ListItemButton
            key={project.id}
            selected={
              hoveredProjectId ? project.id === hoveredProjectId : project.id === selectedProjectId
            }
            onClick={() => onSelectProject(project)}
            onMouseEnter={() => setHoveredProjectId(project.id)}
            sx={{ borderRadius: 1, px: 0.75, py: 0.5 }}
          >
            <Stack spacing={0} sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  lineHeight: 1.2,
                }}
              >
                {project.name}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.1,
                }}
              >
                {project.plant_type ?? 'Plant type unavailable'}
              </Typography>
            </Stack>
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );
}

export default ProjectsMapHoverCard;
